import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Check, AlertTriangle, Loader2, MessageSquare, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApprovalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(searchParams.get('action') || '');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/export/approval/${token}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.approval_status !== 'pending') {
          setSubmitted(true);
          setAction(res.data.approval_status === 'approved' ? 'approve' : 'changes_requested');
        }
      })
      .catch(() => setError('This approval link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!action) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/export/approval/${token}`, {
        action,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
    } catch {
      setError('Failed to submit response. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ABFCF]" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-lg font-bold text-[#0F2040] mb-2">Link Not Found</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const isApprove = action === 'approve';
  const isChanges = action === 'changes_requested';
  const alreadyResponded = info?.approval_status !== 'pending';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="approval-page">
      <div className="bg-white rounded-xl shadow-lg max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-[#0F2040] px-8 py-6">
          <h2 className="text-white text-lg font-bold">Seertech Solutions</h2>
          <p className="text-[#4ABFCF] text-xs tracking-wide mt-1">LMS IMPLEMENTATION ESTIMATOR</p>
        </div>

        <div className="px-8 py-6">
          {submitted ? (
            /* Success state */
            <div className="text-center py-4" data-testid="approval-success">
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                isApprove ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                {isApprove
                  ? <Check className="w-8 h-8 text-emerald-600" />
                  : <AlertTriangle className="w-8 h-8 text-amber-600" />
                }
              </div>
              <h1 className="text-xl font-bold text-[#0F2040] mb-2">
                {alreadyResponded ? 'Response Already Recorded' : 'Response Submitted'}
              </h1>
              <p className="text-sm text-slate-500 mb-1">
                {isApprove
                  ? 'You have approved this Quote.'
                  : 'You have requested changes to this Quote.'
                }
              </p>
              <p className="text-xs text-slate-400 mt-3">
                Quote: <span className="font-medium text-[#0F2040]">{info?.estimate_name}</span>
              </p>
              {info?.approval_comment && (
                <div className="mt-4 bg-slate-50 rounded-lg p-3 text-left">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Your Comment</p>
                  <p className="text-xs text-slate-600">{info.approval_comment}</p>
                </div>
              )}
            </div>
          ) : (
            /* Response form */
            <>
              <p className="text-sm text-slate-500 mb-1">Hello <span className="font-medium text-[#0F2040]">{info?.recipient_name}</span>,</p>
              <p className="text-sm text-slate-500 mb-4">
                <span className="font-medium text-[#0F2040]">{info?.sender_name}</span> has requested your approval for:
              </p>

              <div className="bg-slate-50 rounded-lg px-4 py-3 mb-5">
                <p className="text-base font-bold text-[#0F2040]">{info?.estimate_name}</p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 mb-5">
                <button
                  onClick={() => setAction('approve')}
                  className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all border-2 ${
                    isApprove
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-emerald-600 border-emerald-200 hover:border-emerald-400'
                  }`}
                  data-testid="approval-approve-btn"
                >
                  <Check className="w-4 h-4 inline mr-1.5" />
                  Approve
                </button>
                <button
                  onClick={() => setAction('changes_requested')}
                  className={`flex-1 py-3 rounded-lg font-semibold text-sm transition-all border-2 ${
                    isChanges
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-amber-600 border-amber-200 hover:border-amber-400'
                  }`}
                  data-testid="approval-changes-btn"
                >
                  <AlertTriangle className="w-4 h-4 inline mr-1.5" />
                  Request Changes
                </button>
              </div>

              {/* Comment */}
              <div className="mb-5">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1.5">
                  <MessageSquare className="w-3 h-3" /> Comment <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  rows={3}
                  placeholder={isChanges ? 'Please describe the changes you would like...' : 'Any additional comments...'}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF] resize-y"
                  data-testid="approval-comment"
                />
              </div>

              {error && (
                <p className="text-xs text-red-500 mb-3">{error}</p>
              )}

              <Button
                onClick={handleSubmit}
                disabled={!action || submitting}
                className={`w-full py-2.5 text-sm font-semibold ${
                  isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : isChanges ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#4ABFCF] hover:bg-[#3AAFBF]'
                } text-white`}
                data-testid="approval-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                {submitting ? 'Submitting...' : 'Submit Response'}
              </Button>
            </>
          )}
        </div>

        <div className="px-8 py-3 bg-slate-50 border-t text-center">
          <p className="text-[10px] text-slate-400">Seertech Solutions &middot; Seertech Blueprint Studio &middot; Confidential</p>
        </div>
      </div>
    </div>
  );
}
